import React, { useMemo, useState } from 'react';
import { Avatar, Button, Input, Select } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import SettingsField, { SettingsBlock, SettingsPanel } from '../settings/SettingsField';
import SettingsSegment from '../settings/SettingsSegment';

export default function AssistantTeamSection({
  routerMode,
  onRouterMode,
  contextualize,
  onContextualize,
  members,
  onMembersChange,
  candidates,
  selfId,
}) {
  const [pickId, setPickId] = useState(null);
  const taken = useMemo(() => new Set(members.map((m) => m.assistant_id)), [members]);
  const options = (candidates || [])
    .filter((item) => item.id !== selfId && !item.router_mode && !taken.has(item.id))
    .map((item) => ({ value: item.id, label: item.name }));

  const move = (index, delta) => {
    const next = members.slice();
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    onMembersChange(next.map((rowItem, position) => ({ ...rowItem, position })));
  };

  const add = () => {
    if (pickId == null) return;
    const found = (candidates || []).find((item) => item.id === pickId);
    if (!found) return;
    onMembersChange([
      ...members,
      {
        assistant_id: found.id,
        name: found.name,
        avatar_url: found.avatar_url || null,
        routing_hint: '',
        enabled: true,
        position: members.length,
      },
    ]);
    setPickId(null);
  };

  return (
    <SettingsBlock title="Routing">
      <SettingsPanel>
        <SettingsField
          label="Answers"
          help="Standalone answers on its own. Routes sends each question to the assistants you list."
        >
          <SettingsSegment
            ariaLabel="Routing"
            options={[
              { value: 'standalone', text: 'Standalone' },
              { value: 'routes', text: 'Routes' },
            ]}
            value={routerMode ? 'routes' : 'standalone'}
            onChange={(value) => onRouterMode(value === 'routes')}
          />
        </SettingsField>
        {routerMode && (
          <SettingsField
            label="Follow-ups"
            help="Use the conversation so a short reply still reaches the right specialist."
          >
            <SettingsSegment
              ariaLabel="Follow-ups"
              options={[
                { value: 'on', text: 'On' },
                { value: 'off', text: 'Off' },
              ]}
              value={contextualize ? 'on' : 'off'}
              onChange={(value) => onContextualize(value === 'on')}
            />
          </SettingsField>
        )}
        {routerMode && members.map((member, index) => (
          <div key={member.assistant_id} className="settings-field-row is-stack">
            <div className="settings-member-head">
              <div className="settings-member-id">
                <Avatar size={28} src={member.avatar_url || undefined}>
                  {(member.name || '?').slice(0, 1)}
                </Avatar>
                <span className="settings-field-name">{member.name}</span>
              </div>
              <div className="settings-list-actions">
                <Button
                  className="settings-icon-btn"
                  type="text"
                  icon={<ArrowUpOutlined />}
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                />
                <Button
                  className="settings-icon-btn"
                  type="text"
                  icon={<ArrowDownOutlined />}
                  aria-label="Move down"
                  disabled={index === members.length - 1}
                  onClick={() => move(index, 1)}
                />
                <Button
                  className="settings-icon-btn"
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label="Remove"
                  onClick={() => onMembersChange(members.filter((row) => row.assistant_id !== member.assistant_id))}
                />
              </div>
            </div>
            <Input
              value={member.routing_hint || ''}
              placeholder="When to use"
              maxLength={500}
              onChange={(event) => {
                const hint = event.target.value;
                onMembersChange(members.map((row, rowIndex) => (
                  rowIndex === index ? { ...row, routing_hint: hint } : row
                )));
              }}
            />
          </div>
        ))}
        {routerMode && (
          <div className="settings-panel-add">
            <Select
              showSearch
              allowClear
              placeholder="Add a specialist"
              options={options}
              value={pickId}
              onChange={(value) => setPickId(value ?? null)}
              optionFilterProp="label"
            />
            <Button icon={<PlusOutlined />} onClick={add} disabled={pickId == null}>
              Add
            </Button>
          </div>
        )}
      </SettingsPanel>
    </SettingsBlock>
  );
}
